import React, { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, Col, List, Row, Tooltip, notification } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { ApplicationState } from 'src/store'
import { currencyMask } from 'src/components/Formik'
import { Empty, PageHeader } from 'src/components/Ant'
import {
  Id,
  Reason,
  RequestTicket,
  RequestTicketReceipt,
  StandardType
} from 'src/types'
import { setDraft } from 'src/core/store/ducks/requests/actions'
import * as TicketsService from 'src/services/api/tickets'
import history from 'src/config/routes/history'
import ModalLinkReason from 'src/views/Management/policies/form/modal-link-reason'
import useDocumentTitle from 'src/hooks/use-document-title'
import { sanitizeEditTicket } from './sanitize-edit-ticket'
import * as Css from './styles'
import ModalLinkRequestCreated from './modal-link-request-created'
import ModalTicket from './modal-ticket'
import ThumbnailPreview from '../../components/Receipts/components/ThumbnailPreview'
import {
  updateRequestTicketReceipt,
  UpdateRequestTicketReceiptPayload
} from 'src/services/api/toodo.services'
import { ClientNotifyCatch } from 'src/config/bugsnag'

export type CheckedTickets = RequestTicket & {
  checked: boolean
  organization_id?: Id
}

const MERCHANT_NAME_SIZE_TO_CROP = 25

type RenderReceiptValueProps = {
  request_ticket_receipt: RequestTicketReceipt
}

type RenderListProps = {
  editTicket: any
  isLoading: boolean
  setList: React.Dispatch<React.SetStateAction<CheckedTickets[]>>
  setPreviewImage: React.Dispatch<React.SetStateAction<string>>
  tickets: CheckedTickets[]
}

type RenderItemProps = {
  checkTicket: (Id: Id) => void
  editTicket: any
  setPreviewImage: React.Dispatch<React.SetStateAction<string>>
  ticket: CheckedTickets
}

type RenderImagesProps = {
  receipts: RequestTicketReceipt[]
  setPreviewImage: React.Dispatch<React.SetStateAction<string>>
}

function satizedToUpdate(data: CheckedTickets) {
  const { organization_id, user_id, request_ticket_receipts } = data
  const {
    service,
    currency,
    payment_resource,
    reference,
    request_ticket_receipt_images
  } = request_ticket_receipts?.[0]

  const payload: UpdateRequestTicketReceiptPayload = {
    organization_id,
    user_id,
    receipt: {
      reference_id: +reference?.reference_id || 0,
      currency_id: +currency?.currency_id || 0,
      payment_resource_id: +payment_resource?.payment_resource_id || 0,
      service_id: +service?.service_id || 0,
      justification: 'Testes'
    }
  }

  if (request_ticket_receipt_images.length) {
    payload.image = request_ticket_receipt_images.map(image => ({
      request_ticket_receipt_image_id: !image.request_ticket_receipt_image_id
        ? null
        : +image.request_ticket_receipt_image_id,
      image_base64: image.image_url,
      active: image.active
    }))
  }

  return payload
}

function ListHeader() {
  const { t } = useTranslation()
  return (
    <div>
      <h2>{t('web.single-ticket.list-title')}</h2>
      <Css.Text>{t('web.single-ticket.list-info')}</Css.Text>
    </div>
  )
}

function onlyCheckedTickets(ticket: CheckedTickets) {
  return ticket.checked
}

function RenderImages(props: RenderImagesProps) {
  const imagesList = props.receipts.reduce(
    (acc, cur) => [
      ...acc,
      ...cur.request_ticket_receipt_images
        .filter(item => item.active)
        .map(item => item.image_url)
    ],
    [] as string[]
  )

  if (!imagesList.length) {
    return <Css.ListItemImageSpan />
  }

  return (
    <Css.ListItemImageSpan>
      <Css.ListItemImage
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          props.setPreviewImage(imagesList[0])
        }}
        src={imagesList[0]}
      />
    </Css.ListItemImageSpan>
  )
}

function RenderReceiptValue(props: RenderReceiptValueProps) {
  const serviceName = props.request_ticket_receipt.service?.name
  let text = ''

  if (props.request_ticket_receipt.service?.standard_type === StandardType.KM) {
    text = props.request_ticket_receipt.quantity.toString()
  } else {
    text = currencyMask({
      formatStr: true,
      precision: 2,
      value: props.request_ticket_receipt.amount
    }).maskedValue
    if (props.request_ticket_receipt.currency) {
      text = `${props.request_ticket_receipt.currency.code} ${text}`
    }
  }

  return (
    <div>
      {serviceName && (
        <>
          <Css.Text>{serviceName}</Css.Text>
          <br />
        </>
      )}
      <Css.ListItemAmount>{text}</Css.ListItemAmount>
    </div>
  )
}

function RenderItem(props: RenderItemProps) {
  const request_ticket_receipt =
    props.ticket.request_ticket_receipts?.[0] ?? ({} as RequestTicketReceipt)

  const needCompleted = [
    request_ticket_receipt.currency_id,
    request_ticket_receipt.reference_id,
    request_ticket_receipt.payment_resource_id,
    request_ticket_receipt.service_id
  ].includes(null)

  return (
    <Css.ListItem
      onClick={() => {
        if (!needCompleted) {
          props.checkTicket(props.ticket.request_ticket_id)
        }
      }}
    >
      <Checkbox
        checked={props.ticket.checked}
        disabled={needCompleted}
        value={props.ticket.request_ticket_id}
      />
      <Css.Text>#{props.ticket.request_ticket_number ?? ''}</Css.Text>
      <Css.Text>
        {moment.utc(request_ticket_receipt.date * 1000).format('DD/MM/YY')}
      </Css.Text>
      <Tooltip title={props.ticket.merchant_name}>
        <Css.Text>
          {props.ticket.merchant_name.length <= MERCHANT_NAME_SIZE_TO_CROP
            ? props.ticket.merchant_name
            : `${props.ticket.merchant_name.substring(
                0,
                MERCHANT_NAME_SIZE_TO_CROP - 3
              )}...`}
        </Css.Text>
      </Tooltip>
      <RenderReceiptValue request_ticket_receipt={request_ticket_receipt} />
      <RenderImages
        receipts={props.ticket.request_ticket_receipts}
        setPreviewImage={props.setPreviewImage}
      />
      <Css.Icon
        type="edit"
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          props.checkTicket(props.ticket.request_ticket_id)
          props.editTicket(props.ticket)
        }}
      />
    </Css.ListItem>
  )
}

function RenderList(props: RenderListProps) {
  function checkTicket(id: Id): void {
    props.setList(oldList =>
      oldList.map(item =>
        item.request_ticket_id === id
          ? { ...item, checked: !item.checked }
          : item
      )
    )
  }

  return (
    <List
      dataSource={props.tickets}
      header={<ListHeader />}
      loading={props.isLoading}
      locale={{ emptyText: <Empty /> }}
      renderItem={ticket => (
        <RenderItem
          checkTicket={checkTicket}
          editTicket={props.editTicket}
          key={ticket.request_ticket_id}
          setPreviewImage={props.setPreviewImage}
          ticket={ticket}
        />
      )}
    />
  )
}

export default function ExpenseClassification() {
  const {
    me: {
      organization: { organization_id },
      user: { user_id, tenant_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  useDocumentTitle('web.single-ticket.page-title')

  const isSubscribed = useRef(true)
  const [editTicket, setEditTicket] = useState<CheckedTickets>(null)
  const [previewImage, setPreviewImage] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)
  const [openModalNewRequest, setOpenModalNewRequest] = useState(false)
  const [openModalRequestCreated, setOpenModalRequestCreated] = useState(false)
  const [ticketsList, setTicketsList] = useState<CheckedTickets[]>([])
  const dispatch = useDispatch()
  const { t } = useTranslation()

  function updateList() {
    setIsLoading(true)
    TicketsService.getToodoPayTickets({
      organization_id,
      user_id
    })
      .then(response => {
        if (!isSubscribed.current) return

        if (response.status === 200) {
          setTicketsList(
            response.data.request_tickets.map((item: RequestTicket) => ({
              ...item,
              checked: false
            })) as CheckedTickets[]
          )
          return
        }

        notification.error({
          duration: 8,
          message:
            typeof response.data?.reasons === 'string'
              ? response.data?.reasons
              : t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .catch(response => {
        if (!isSubscribed.current) return
        notification.error({
          duration: 8,
          message:
            typeof response.data?.reasons === 'string'
              ? response.data?.reasons
              : t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        if (!isSubscribed.current) return
        setIsLoading(false)
      })
  }

  useEffect(() => {
    return () => {
      isSubscribed.current = false
    }
  }, [])

  useEffect(() => {
    updateList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function modalNewRequestSubmit(values: {
    reason_id: string
    reason: Reason
  }) {
    if (values.reason_id) {
      dispatch(setDraft(null))
      const request_tickets = ticketsList.filter(onlyCheckedTickets)

      history.push(`/my-requests/create/reason/${values.reason_id}`, {
        request_tickets
      })
    }
  }

  const hasTicketSelected = ticketsList.some(onlyCheckedTickets)

  async function handleSubmit(values: CheckedTickets) {
    const sanitizedValues = sanitizeEditTicket(values)
    sanitizedValues.checked = true
    setTicketsList(oldValues =>
      oldValues.map(ticket =>
        ticket.request_ticket_id.toString() ===
        sanitizedValues.request_ticket_id.toString()
          ? sanitizedValues
          : ticket
      )
    )
    setEditTicket(null)

    const { request_ticket_id } = sanitizedValues
    const request_ticket_receipt_id =
      sanitizedValues.request_ticket_receipts[0].request_ticket_receipt_id

    const updateReceipt = satizedToUpdate(sanitizedValues)
    try {
      await updateRequestTicketReceipt(
        request_ticket_id,
        request_ticket_receipt_id,
        updateReceipt
      )
      notification.success({
        message: t('common.edit-done-successfully'),
        placement: 'bottomRight'
      })
    } catch (error) {
      ClientNotifyCatch('Update expense-classification when saved it', error)
      //@ts-ignore
      const reasons = error?.response?.data?.detail
      const message =
        typeof reasons === 'string' ? reasons : t('common.response.status-500')
      notification.error({
        message,
        placement: 'bottomRight',
        duration: 10
      })
    }
  }

  const canGenerateNewRequest = ![559042084949].includes(+tenant_id)

  return (
    <>
      <PageHeader
        extra={[
          ...(canGenerateNewRequest
            ? [
                <Button
                  data-test-id="new-request"
                  disabled={!hasTicketSelected}
                  key="new-request"
                  loading={isLoading}
                  onClick={() => {
                    setOpenModalNewRequest(true)
                  }}
                  size="default"
                  type="dashed"
                >
                  {t('web.new-request.title-section')}
                </Button>
              ]
            : []),
          <Button
            data-test-id="old-request"
            disabled={!hasTicketSelected}
            key="old-request"
            loading={isLoading}
            onClick={() => {
              setOpenModalRequestCreated(true)
            }}
            size="default"
            type="dashed"
          >
            {t('web.single-ticket.old-request')}
          </Button>
        ]}
        onBack={() => history.goBack()}
        title="web.single-ticket.page-title"
      />
      <Row>
        <Col>
          <Css.Card>
            <RenderList
              editTicket={setEditTicket}
              isLoading={isLoading}
              setList={setTicketsList}
              setPreviewImage={setPreviewImage}
              tickets={ticketsList}
            />
          </Css.Card>
        </Col>
      </Row>
      <ModalLinkReason
        onSubmit={modalNewRequestSubmit}
        toggle={() => setOpenModalNewRequest(false)}
        visible={openModalNewRequest}
      />
      <ModalLinkRequestCreated
        modalClose={() => setOpenModalRequestCreated(false)}
        organization_id={organization_id}
        ticketsList={ticketsList.filter(onlyCheckedTickets)}
        updateRequestList={updateList}
        user_id={user_id}
        visible={openModalRequestCreated}
      />
      <ModalTicket
        handleSubmit={handleSubmit}
        organization_id={organization_id}
        ticket={editTicket}
        toggle={() => setEditTicket(null)}
      />
      <ThumbnailPreview
        initPreview={previewImage}
        onCancel={() => setPreviewImage(null)}
      />
    </>
  )
}
